import React, { FocusEventHandler } from 'react';
import { Heading } from '@mos-cat/typography';
import { t, Trans } from '@lingui/macro';
import { connect } from 'dva';
import { Dispatch } from 'redux';
import Select from '@mos-front-components/public/es/components/MosSelect';
import TextField from '../../shared/TextField/TextField';
import Checkbox from '@mos-cat/checkbox';
import { getValue } from '../../../constants/getFieldValues';
import renderOrganizationSuggestion from './helpers/renderOrganizationSuggestion';
import checkForDisabledValue from './helpers/checkValue';
import { ORGANIZATION_FIELD_NAME } from '../../../constants/fieldNames';
import { i18n } from '../../../helpers/i18n';
import { getPayload } from '../../../helpers/handlers';
import { Organization } from '../../../helpers/initialState';
import { SPECIAL_VALUE } from '../../../helpers/address';

interface Props {
  polyclinic: Organization;

  onPolyclinicChange(name, value): void;

  onInputChange(value, method, event): void;

  setFromSuggest(value): void;

  onBlur(payload: boolean): void;
  onPolyclinicBlur: FocusEventHandler;
}

function Polyclinic({
  polyclinic,
  onPolyclinicChange,
  onPolyclinicBlur,
  onInputChange,
  setFromSuggest,
  onBlur,
}: Props): JSX.Element {
  const { isFoundInCatalog, institutionName, suggestions } = polyclinic;

  const institutionNameValue = getValue(institutionName);
  const placeholder = i18n._(t`Наименование учреждения здравоохранения`);

  const placeholderForInput = institutionNameValue.trim().length
    ? placeholder
    : i18n._(t`Введите наименование учреждения здравоохранения`);

  const disableSelect = !isFoundInCatalog;
  const selectValue = disableSelect ? '' : institutionNameValue;
  const handleBlur = (event, { highlightedSuggestion }) => {
    onBlur(!suggestions.includes(highlightedSuggestion));
  };

  return (
    <div className="Polyclinic StepContentBlock">
      <Heading level={3} className="PguFormGrid__row">
        <Trans>Сведения о наблюдении ребёнка в медицинской организации</Trans>
      </Heading>
      <div className="PguFormGrid__row">
        <Select
          className="PguFormGrid_col ym-hide-content"
          name={ORGANIZATION_FIELD_NAME.institutionName}
          placeholder={placeholder}
          suggestions={suggestions}
          value={selectValue}
          hasError={institutionName.error}
          onChange={setFromSuggest}
          onInputChange={onInputChange}
          renderSuggestion={renderOrganizationSuggestion}
          disabled={disableSelect}
          onBlur={handleBlur}
          withClear
        />
      </div>
      <div className="PguFormGrid__row">
        <Checkbox
          name={ORGANIZATION_FIELD_NAME.isFoundInCatalog}
          checked={!isFoundInCatalog}
          label={i18n._(t`Учреждения нет в списке`)}
          onChange={({ target: { name, checked } }) =>
            onPolyclinicChange(name, !checked)
          }
          value={ORGANIZATION_FIELD_NAME.isFoundInCatalog}
        />
      </div>
      {!isFoundInCatalog && (
        <div className="PguFormGrid__row">
          <TextField
            className="PguFormGrid_col"
            placeholder={placeholderForInput}
            inputProps={{
              name: ORGANIZATION_FIELD_NAME.institutionName,
              ...institutionName,
              onChange: ({ target: { name, value } }) =>
                onPolyclinicChange(name, { value }),
              onBlur: onPolyclinicBlur,
              maxLength: 500,
            }}
          />
        </div>
      )}
    </div>
  );
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onPolyclinicChange(name, value) {
    dispatch({
      type: 'child/polyclinic/change',
      payload: { [name]: value },
    });
  },
  onInputChange(value, method) {
    if (['click', 'enter'].includes(method)) {
      return;
    }
    if (['up', 'down'].includes(method)) {
      dispatch({
        type: `child/polyclinic/set`,
        payload: value,
      });
      return;
    }
    if (!method) {
      dispatch({
        type: 'child/polyclinic/change',
        payload: { [ORGANIZATION_FIELD_NAME.institutionName]: { value: '' } },
      });
      return;
    }
    dispatch({
      type: 'child/polyclinic/change',
      payload: { [ORGANIZATION_FIELD_NAME.institutionName]: { value } },
    });
  },
  setFromSuggest(suggest): void {
    const { value, text } = suggest;
    const isDisableValue = checkForDisabledValue(value);
    if (!text || isDisableValue) return;

    if (SPECIAL_VALUE[value] === SPECIAL_VALUE[SPECIAL_VALUE.retry]) {
      dispatch({
        type: 'child/polyclinic/retry',
      });
      return;
    }
    if (SPECIAL_VALUE[value] === SPECIAL_VALUE[SPECIAL_VALUE.switchToManual]) {
      dispatch({
        type: 'child/polyclinic/change',
        payload: { [ORGANIZATION_FIELD_NAME.isFoundInCatalog]: false },
      });
      return;
    }

    dispatch({ type: 'child/polyclinic/set', payload: text });
  },
  onBlur(payload) {
    dispatch({ type: `child/polyclinic/institutionName/setError`, payload });
  },
  onPolyclinicBlur(event) {
    dispatch({
      type: 'child/polyclinic/change',
      payload: getPayload(event, true),
    });
  },
});

export default connect(
  ({ child: { polyclinic } }) => ({ polyclinic }),
  mapDispatchToProps
)(Polyclinic);
